import { useReactiveVar } from '@apollo/client';
import { Keyboard } from '@capacitor/keyboard';
import PubNub from 'pubnub';
import { PubNubProvider } from 'pubnub-react';
import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';

import { Role } from '../PrivateRoute';

import BottomNav from './BottomNav';
import MessageContainer from './MessageContainer';
import UserDefaultPubNubChannel from './UserDefaultPubNubChannel';

import AppFooter from '@/components/AppFooter';
import AppFrame from '@/components/AppFrame';
import LoadingState from '@/components/LoadingState';
import Config from '@/config';
import { V3Endpoints } from '@/constants/urls';
import { useGetAgencyByKeyQuery } from '@/graphql';
import { useAppDispatch, useAppSelector } from '@/hooks/store';
import useMediaQuery from '@/hooks/useMediaQuery';
import Sidebar from '@/routes/Agency/Sidebar';
import {
  setKeyboardVisible,
  setPendingAiAssessmentsCount,
  setPendingCustomers,
  setPendingCertificatesCount,
  setPendingUniformsCount,
  setSidebarVisibility,
} from '@/store/slices/layoutSlice';
import { accountabilityAPI } from '@/services/accountabilityAPI';
import {
  currentAgencyVar,
  currentTenantVar,
  currentAdminVar,
} from '@/util/apollo/cache';
import axiosClient, { axiosRubyClient } from '@/util/axios/axiosClient';
import { CustomerCountResponse } from '@/util/axios/types';
import { isWeb } from '@/util/platform';
import { identifyAdmin } from '@/util/vexo';
import AppNavbarV2 from '@/components/AppNavbarV2';
import AppNavbar from '@/components/AppNavbar';
import AppFrameV2 from '@/components/AppFrameV2';
import SidebarV2 from '../Agency/SidebarV2';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { FEATURE_TOGGLE } from '@/constants/featuretoggle';

type PendingUniformsResponse = {
  uniforms?: unknown[];
};

type PendingCertificatesResponse = {
  photos?: unknown[];
};

type PendingAiAssessmentsResponse = {
  data?: unknown[];
  pagination?: {
    total?: number;
  };
};

const Agency = () => {
  const dispatch = useAppDispatch();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const currentTenant = useReactiveVar(currentTenantVar);
  const currentAgency = useReactiveVar(currentAgencyVar);
  const currentAdmin = useReactiveVar(currentAdminVar);
  const isSmallDesktop = useMediaQuery('(min-width: 900px)');

  const query = useGetAgencyByKeyQuery({
    variables: { tenantId: currentTenant!.id, key: 'gravy-dmv' },
  });

  const keyboardVisible = useAppSelector(
    (state) => state.layout.keyboardVisible,
  );
  useEffect(() => {
    if (query.data) {
      currentAgencyVar(query.data.agencyByKey);
      // Re-identify so the new account_id (= agency.id) lands in Vexo's
      // session profile. Custom events already pick up the new value via
      // baseProps, but identify is per-session and needs the explicit call.
      identifyAdmin();
    }
  }, [query.data]);

  const [pubnub, setPubnub] = useState();

  useEffect(() => {
    if (currentAdmin && !pubnub) {
      setPubnub(
        new PubNub({
          publishKey: Config.PUBNUB_PUBLISH_KEY,
          subscribeKey: Config.PUBNUB_SUBSCRIBE_KEY,
          userId: `${currentAdmin?.user.id}`,
          restore: true,
        }),
      );
    }
  }, [currentAdmin]);

  useEffect(() => {
    if (isSmallDesktop) {
      dispatch(setSidebarVisibility(true));
    } else {
      dispatch(setSidebarVisibility(false));
    }
  }, [isSmallDesktop]);

  useEffect(() => {
    if (!isWeb()) {
      dispatch(setKeyboardVisible(false));
      Keyboard.addListener('keyboardWillShow', () => {
        dispatch(setKeyboardVisible(true));
      });

      Keyboard.addListener('keyboardWillHide', () => {
        dispatch(setKeyboardVisible(false));
      });

      return () => {
        Keyboard.removeAllListeners();
      };
    }
  }, []);

  useEffect(() => {
    if (currentAdmin && currentAdmin!.role === Role.TENANT_ADMIN) {
      axiosClient
        .get<CustomerCountResponse>(V3Endpoints.GET_CUSTOMERS_COUNT, {
          params: { status: 0 },
        })
        .then(({ data }) => {
          if (data && data.count) {
            dispatch(setPendingCustomers(true));
          } else {
            dispatch(setPendingCustomers(false));
          }
        })
        .catch((err) => {
          console.error(err);
          dispatch(setPendingCustomers(false));
        });

      axiosRubyClient
        .get<PendingUniformsResponse>('/api/admin/worker_uniforms', {
          params: { status: 'pending' },
        })
        .then(({ data }) => {
          dispatch(
            setPendingUniformsCount(
              Array.isArray(data?.uniforms) ? data.uniforms.length : 0,
            ),
          );
        })
        .catch((err) => {
          console.error(err);
          dispatch(setPendingUniformsCount(0));
        });

      axiosRubyClient
        .get<PendingCertificatesResponse>('/api/admin/certificate_photos', {
          params: { status: 'pending' },
        })
        .then(({ data }) => {
          dispatch(
            setPendingCertificatesCount(
              Array.isArray(data?.photos) ? data.photos.length : 0,
            ),
          );
        })
        .catch((err) => {
          console.error(err);
          dispatch(setPendingCertificatesCount(0));
        });

      axiosRubyClient
        .get<PendingAiAssessmentsResponse>('/api/admin/assessments', {
          params: {
            reviewed: false,
            include_pending: true,
            page: 1,
            per_page: 1,
          },
        })
        .then(({ data }) => {
          const total =
            typeof data?.pagination?.total === 'number'
              ? data.pagination.total
              : Array.isArray(data?.data)
                ? data.data.length
                : 0;
          dispatch(setPendingAiAssessmentsCount(total));
        })
        .catch((err) => {
          console.error(err);
          dispatch(setPendingAiAssessmentsCount(0));
        });
    } else {
      dispatch(setPendingCustomers(false));
      dispatch(setPendingUniformsCount(0));
      dispatch(setPendingCertificatesCount(0));
      dispatch(setPendingAiAssessmentsCount(0));
    }
  }, [currentAdmin]);

  const useNavigationImprovements = useFeatureValue(
    FEATURE_TOGGLE.NavigationImprovements,
    false,
  );

  return pubnub ? (
    <PubNubProvider client={pubnub}>
      <UserDefaultPubNubChannel>
        {useNavigationImprovements ? (
          <AppFrameV2
            BottomNav={!isWeb() && !keyboardVisible && <BottomNav />}
            Footer={<AppFooter />}
            Navbar={<AppNavbarV2 />}
            Sidebar={
              <SidebarV2
                isCollapsed={isCollapsed}
                onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
              />
            }
            isSmallDesktop={isSmallDesktop}
            isCollapsed={isCollapsed}
          >
            {currentAgency ? (
              <>
                <MessageContainer />
                <Outlet />
              </>
            ) : (
              <LoadingState text="Loading your agency's data..." />
            )}
          </AppFrameV2>
        ) : (
          <AppFrame
            BottomNav={!isWeb() && !keyboardVisible && <BottomNav />}
            Footer={<AppFooter />}
            Navbar={<AppNavbar />}
            Sidebar={
              <Sidebar
                isCollapsed={isCollapsed}
                onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
              />
            }
            isSmallDesktop={isSmallDesktop}
            isCollapsed={isCollapsed}
          >
            {currentAgency ? (
              <>
                <MessageContainer />
                <Outlet />
              </>
            ) : (
              <LoadingState text="Loading your agency's data..." />
            )}
          </AppFrame>
        )}
      </UserDefaultPubNubChannel>
    </PubNubProvider>
  ) : (
    <LoadingState text="Loading..." />
  );
};

export default Agency;
